import {Component, OnDestroy, ChangeDetectionStrategy, HostListener} from '@angular/core';
import {DocumentModel} from "../../../model/document.model";
import {ActivatedRoute, Router} from "@angular/router";
import {buffer, debounceTime, delay, filter, first, map, Subject, takeUntil, tap} from "rxjs";
import {DmxDocumentService} from "../../../service/http/dmx-document.service";
import {DocumentDetailActionComponent} from "./document-detail-action/document-detail-action.component";
import {LayoutElementUtil} from "../../../service/layout/layout-element.service";
import {ObservableWrapper} from "../../util/observable-wrapper";
import {MatDialog} from "@angular/material/dialog";
import {RfaStore} from "../../../store/rfa/rfa.store";
import {EventService} from "../../../service/event.service";
import {DocumentDetailElementsComponent} from './document-detail-elements/document-detail-elements.component';
import {MatTabsModule} from '@angular/material/tabs';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {LoadingErrorComponent} from '../../common/loading/loading-error/loading-error.component';
import {P_DOCUMENTS} from "../../../app.routes";
import {fadeIn} from "../../../animations/animations";
import {Container, KiButton, KiButtonMapping, Tab, TextArea} from "../../../model/layout.model";
import {ProgressbarService} from "../../../service/progressbar.service";
import {EventBusService} from "../../../service/event-bus.service";
import {CosmoUebernahme, GrafikUmfang, KiRefreshEvent, MosOhneWerte} from "../../../model/event-bus.model";
import {EventBusTypes} from "../../../model/constants";
import {DocumentSaveService} from "../../../service/document-save.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DocumentStore} from "../../../store/document.store";
import {CosmoTakeoverService} from "../../../service/cosmo-takeover.service";
import {PrognoseUebernahmeService} from "../../../service/prognose-uebernahme.service";
import {KiTexteTakeoverService, KiTexteZiel} from "../../../service/ki-texte-takeover.service";
import {KiGrafikService} from "../../../service/ki-grafik.service";
import {KiChatService} from "../../../service/http/ki-chat.service";
import {KiLaufService} from "../../../service/ki-lauf.service";
import {KiErgebnisService} from "../../../service/ki-ergebnis.service";
import {KiReihenfolgeService} from "../../../service/ki-reihenfolge.service";
import {SchlagzeileSchalterService} from "../../../service/schlagzeile-schalter.service";
import {LadeQuellenService} from "../../../service/lade-quellen.service";


@Component({
    selector: 'app-document-detail',
    templateUrl: './document-detail.component.html',
    styleUrls: ['./document-detail.component.scss'],
    animations: [fadeIn],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    LoadingErrorComponent,
    MatTabsModule,
    NgTemplateOutlet,
    DocumentDetailElementsComponent,
    AsyncPipe
]
})
export class DocumentDetailComponent implements OnDestroy {

  public wetterTerminalDocumentWrapper: ObservableWrapper<DocumentModel> | null = null;
  public selectedTabIndex: number = 0;

  /** Pause nach der letzten "keine MOS-Werte"-Meldung, nach der die Snackbar erscheint. */
  static readonly MOS_HINWEIS_BUENDEL_MS = 400;

  /** `documentId` in der Route, die "neuen Datensatz anlegen" bedeutet. */
  private static readonly NEUER_DATENSATZ = 'new';

  public documentId: string = DocumentDetailComponent.NEUER_DATENSATZ;
  public selectedRfaKuerzel!: string;
  public readOnly: boolean = false;

  private destroyer$: Subject<boolean> = new Subject<boolean>();

  /** Läuft gerade ein Speichervorgang? Verhindert doppeltes Absenden. */
  private speichertGerade = false;

  constructor(private activatedRoute: ActivatedRoute, private readonly restService: DmxDocumentService,
              private dialog: MatDialog, private eventService: EventService,
              private rfaStore: RfaStore, private route: Router, public layoutElementUtil: LayoutElementUtil,
              private progressbarService: ProgressbarService,
              private eventBusService: EventBusService,
              private saveService: DocumentSaveService,
              private documentStore: DocumentStore,
              private snackBar: MatSnackBar,
              private cosmoTakeover: CosmoTakeoverService,
              private prognoseUebernahme: PrognoseUebernahmeService,
              private kiTexteTakeover: KiTexteTakeoverService,
              private kiGrafik: KiGrafikService,
              private kiChatService: KiChatService,
              private kiLaufService: KiLaufService,
              private kiErgebnisService: KiErgebnisService,
              private ladeQuellenService: LadeQuellenService,
              private kiReihenfolge: KiReihenfolgeService,
              private schlagzeileSchalter: SchlagzeileSchalterService) {

    //ABBONEIREN AUF DIE ÄNDERUNG DER AUSGEWÄHLTEN RFA
    this.rfaStore.getSelectedRfa().pipe(takeUntil(this.destroyer$), map((rundfunkanstalt) => {
      if (rundfunkanstalt) {
        this.selectedRfaKuerzel = rundfunkanstalt.kuerzel;
        console.debug("CONSTRUCTOR DocumentDetailComponent: " + this.selectedRfaKuerzel);
        //ABBONNIERT SICH AUF DIE ROUTE UND BEZIEHT AUS DEN PARAMS DIE DOCUMENTID & GGF. DAS COPYPROFILE
        this.activatedRoute.queryParams
          .pipe(takeUntil(this.destroyer$))
          .subscribe({

            next: params => this.getDocument(this.documentId = params['documentId'], params['copyProfile'])
            ,
            error: err => console.error(`ERROR: fetching queryParams for documentId`, err),
          });
      }
    })).subscribe();


    //ABBONNIERT SICH AUF DEN EVENTSERVICE UND ÖFFNET DAS ACTION SHEET
    this.eventService.getShowPublishActions().pipe(takeUntil(this.destroyer$)).subscribe({
      next: (data) => {
        if (data.show) this.showActions();
      }
    });

    //SPEICHERN OHNE DIALOG: Knopf in der Toolbar bzw. Strg+S
    this.eventBusService.on$(EventBusTypes.SAVE_EVENT.valueOf()).pipe(takeUntil(this.destroyer$)).subscribe({
      next: () => this.speichern()
    });

    //"für COSMO übernehmen": die Grafik-Werte nach COSMO kopieren – der ganze Reiter
    //(Knopf oben) oder nur eine Zeile (Knopf an deren Überschrift)
    this.eventBusService.on$<CosmoUebernahme>(EventBusTypes.COSMO_TAKEOVER_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$)).subscribe({
        next: (auftrag) => this.fuerCosmoUebernehmen(auftrag)
      });

    //"gespeicherte Daten laden" in der Leiste von "Tendenzen/Prognosen" (Lokalzeiten): die
    //TV-Werte der drei Aussichtentage in die Spalten der Prognose-Karte – die Quellen liegen in
    //anderen Reitern, deshalb hier, wo das Dokument vorliegt.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.PROGNOSE_UEBERNAHME_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$)).subscribe({
        next: (umfang) => this.gespeicherteDatenLaden(umfang)
      });

    //"für Videotext übernehmen" / "für COSMO übernehmen" im Reiter "KI-Texte": stumpfer
    //Kopiervorgang der ausgefüllten KI-Texte, keine KI-Anfrage.
    this.eventBusService.on$(EventBusTypes.VIDEOTEXT_TAKEOVER_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$)).subscribe({
        next: () => this.kiTexteUebernehmen('videotext')
      });
    this.eventBusService.on$(EventBusTypes.COSMO_TEXT_TAKEOVER_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$)).subscribe({
        next: () => this.kiTexteUebernehmen('cosmo')
      });

    //"KI-Texte erzeugen" über den NRW-Grafiken: angehakte Boxen -> Schlagzeile im
    //Langwetter. Der Umfang sagt, ob alle Zeilen gemeint sind oder nur eine.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.KI_GRAFIK_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$)).subscribe({
        next: (umfang) => this.erzeugeKiTextAusGrafik(umfang)
      });

    //"MSWR-MOS laden" auf einer Zeile, für die die MOS-Datei nichts hergibt (Lokalzeit
    //"Heute", Aussichtentage): jede Karte meldet einzeln, hier wird je Knopfdruck zu EINER
    //Snackbar gebündelt – Heute sind 6 Zeilen × 3 Karten, 18 Snackbars helfen niemandem.
    //Gesammelt wird, bis nach der letzten Meldung eine Pause liegt (buffer + debounceTime),
    //nicht in einem festen Raster: die Antworten der Karten treffen gestaffelt ein.
    const mosOhneWerte$ = this.eventBusService.on$<MosOhneWerte>(EventBusTypes.MOS_OHNE_WERTE_EVENT.valueOf());
    mosOhneWerte$
      .pipe(
        buffer(mosOhneWerte$.pipe(debounceTime(DocumentDetailComponent.MOS_HINWEIS_BUENDEL_MS))),
        filter(meldungen => meldungen.length > 0),
        takeUntil(this.destroyer$)
      ).subscribe({
        next: (meldungen) => this.meldeMosOhneWerte(meldungen)
      });

    this.eventBusService.on$<KiRefreshEvent>(EventBusTypes.KI_REFRESH_EVENT.valueOf()).pipe(takeUntil(this.destroyer$)).subscribe(kiRefreshEvent => {
      this.wetterTerminalDocumentWrapper?.data$.pipe(takeUntil(this.destroyer$)).subscribe({
        next: (document) => {
          //console.log('KI-REFRESH-EVENT: '+ JSON.stringify(kiRefreshEvent));
          // Erst der Grafik-Weg: die Textboxen im Reiter "KI-Texte" hängen an keinem
          // KiButton-Mapping, ihre Anfrage wird aus dem Reiter rekonstruiert.
          if (this.erneuereKiTextAusGrafik(document, kiRefreshEvent)) {
            return;
          }
          document.tabs.forEach((tab: Tab) => {
            let kiButtonContainer:Container|null = this.findContainerByContainerType(tab, 'KiButton');
            if(kiButtonContainer && this.layoutElementUtil.isKiButton(kiButtonContainer)) {
              //console.log('KI-REFRESH-EVENT KI-BUTTON FOUND: '+ JSON.stringify(c));

              const kiButton:KiButton = this.layoutElementUtil.asKiButton(kiButtonContainer);
              if(kiButton) {
                const kiButtonMappings:KiButtonMapping[] | undefined = kiButton.kiButtonMappings;
                if (kiButtonMappings && kiButtonMappings.length > 0) {
                  //console.log("FIRE_KI_REFRESH_EVENT KI--BUTTON-MAPPING: " + JSON.stringify(kiButtonMappings));
                  let mapping:KiButtonMapping|undefined = kiButtonMappings.find(value => value.to === kiRefreshEvent.containerId);
                  if(mapping) {
                    let kiButtonMappings2:KiButtonMapping[] = [];
                    if(kiRefreshEvent.shortened) {
                      //console.log("MAPPING GEHT, WEIL SHORTENED ");
                      let maxChars:number = Number.parseInt(mapping.max_chars);
                      maxChars = maxChars - Math.round(maxChars/10);
                      //mapping.max_chars = maxChars.toString();
                      let newMapping:KiButtonMapping|undefined = {
                        from:mapping.from,
                        to:mapping.to,
                        max_chars:maxChars.toString(),
                        maxRetries:mapping.maxRetries,
                        reduceMaxChars:mapping.reduceMaxChars,
                        zusatzinfo:mapping.zusatzinfo
                      }
                      kiButtonMappings2.push(newMapping);
                      //console.log("MAPPING MAX_CHARS: " + mapping.max_chars + " MAXCHARS: " + maxChars);
                    } else {
                      kiButtonMappings2.push(mapping);
                    }

                    //console.log("MAPPING: " + JSON.stringify(mapping) + "CONTAINERID: " + event.containerId + " SHORTEND: " + event.shortened);
                    this.doKiEvent(kiButtonMappings2, document.tabs);
                  }
                }
              }
            }
          })
        }
      });
    });

    this.eventBusService.on$<KiButtonMapping[]>(EventBusTypes.KI_BUTTON_EVENT.valueOf()).pipe(takeUntil(this.destroyer$)).subscribe(kiButtonMappings => {
      this.wetterTerminalDocumentWrapper?.data$.pipe(takeUntil(this.destroyer$)).subscribe({
        next: (document) => {
          this.doKiEvent(kiButtonMappings, document.tabs);
        }
      });
    })
  }

  /**
   * Strg+S (bzw. Cmd+S) speichert. `preventDefault` ist Pflicht, sonst öffnet der Browser
   * seinen eigenen "Seite speichern"-Dialog.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      // Im Nur-Lesen-Modus gibt es auch keinen Speichern-Knopf in der Toolbar.
      if (!this.layoutElementUtil.ReadonlyMode) {
        this.speichern();
      }
    }
  }

  /**
   * Legt den Datensatz so ab, wie er gerade auf der Seite steht.
   *
   * Es muss nichts eingesammelt werden: die Element-Komponenten schreiben ihre Werte
   * direkt in den `Container`-Baum, den der Wrapper hält – auch die Grafik-Boxen, die
   * ihre Werte nach `image.values`/`section.values` spiegeln. Leer gelassene Boxen
   * stehen dort gar nicht, gehen also auch nicht als Leerwert mit.
   *
   * Fehlt im Header ein Workflow (neues Dokument), setzt der Speicherdienst "Alles" –
   * siehe {@link DocumentSaveService.WORKFLOW_ALLES}.
   */
  public speichern(): void {
    if (this.speichertGerade) {
      return;   // Doppelauslösung durch Knopf und Tastenkürzel abfangen
    }
    this.wetterTerminalDocumentWrapper?.data$.pipe(first()).subscribe({
      next: (document) => {
        this.speichertGerade = true;
        this.saveService.speichern(this.selectedRfaKuerzel, document).subscribe({
          next: (gespeichert) => {
            this.documentId = gespeichert.wetterTerminalDocument.header.documentId as string;
            document.header.documentId = this.documentId;
            this.documentStore.addOrUpdateDocument(gespeichert.wetterTerminalDocument.header);
            this.snackBar.open('Datensatz gespeichert', 'OK', {
              duration: 3000, verticalPosition: 'bottom', horizontalPosition: 'right'
            });
            this.speichertGerade = false;
          },
          error: (err) => {
            // Nicht err.message nehmen: das endet auf "500 OK" – siehe DocumentSaveService.fehlermeldung.
            const meldung = DocumentSaveService.fehlermeldung(err);
            this.snackBar.open(`Speichern fehlgeschlagen: ${meldung}`, 'OK');
            console.error(`ERROR: Speichern fehlgeschlagen (${meldung})`, err);
            this.speichertGerade = false;
          }
        });
      }
    });
  }

  /**
   * "KI-Texte erzeugen" über den Grafiken.
   *
   * Sammelt die Werte **aller angehakten Boxen** (gelber Umschalter `ai`) des Dokuments,
   * schickt sie als JSON-Baum an den KI-Service und schreibt die Antwort in die
   * **Textbox seines Reiters** im Reiter "KI-Texte" ("Heute" -> "Heute",
   * "2.Aussichtentag" -> "2. Aussichtentag").
   *
   * Dazu läuft eine **zweite** Anfrage für die **Schlagzeile**: sie gehört zu keinem
   * einzelnen Reiter, sondern fasst alle Zeiträume zusammen – siehe
   * {@link erzeugeSchlagzeile}.
   *
   * Geht, wie der Videotext-Knopf "Daten übernehmen" (`POST chat/hrtext`), über den
   * wetter-terminal-rest-service ({@link KiChatService}) – der KI-Springboot-Service
   * selbst ist zur Laufzeit nicht direkt erreichbar und wird nur als Durchlauferhitzer
   * angesprochen.
   *
   * Ist keine Box angehakt, passiert bewusst nichts außer einer Meldung: der Knopf soll
   * nicht stillschweigend wirkungslos sein.
   *
   * @param umfang Welche Karten gelesen werden: die des **Reiters** (der Knopf in der
   *   Leiste oben) oder die einer **Zeile** (der Knopf an deren Überschrift). Nie das
   *   ganze Dokument – jeder Reiter ist ein anderer Zeitpunkt, und eine Anfrage über
   *   alle Reiter erzeugte Texte über den falschen Zeitraum. Die Antwort landet in der
   *   grünen Box desselben Umfangs **und** in der Textbox seines Reiters.
   */
  public erzeugeKiTextAusGrafik(ausloeser: GrafikUmfang): void {
    this.wetterTerminalDocumentWrapper?.data$.pipe(first()).subscribe({
      next: (document) => {
        // Der Knopf an einer Zeile tut dasselbe wie der oben am Reiter: er nimmt alle Zeilen
        // des Reiters, in denen ein Symbol angehakt ist – nicht nur die eigene. Die Antwort
        // erscheint deshalb auch unter der Leiste oben.
        const umfang = this.reiterUmfangVon(document, ausloeser);
        // Gelesen werden die Karten der Ziel-Textbox: meist genau die des Reiters, bei
        // COSMO "Aussichten" die aller drei Aussichten-Reiter – die grüne Box bleibt aber
        // die des Reiters, in dem der Knopf gedrückt wurde.
        const zielId = this.kiGrafik.zielFuer(umfang);
        const zielKarten = zielId ? this.kiGrafik.kartenFuerZiel(document, zielId) : [];
        // Die Lokalzeit-Prognose sendet nur ihre `kiFeld`-Boxen und kennt keine angehakten
        // Zeilen (ihre Boxen haben keine Umschalter) – die Zeilen-Auswahl entfällt dort.
        const auswahl = KiGrafikService.auswahlFuer(zielId);
        const ergebnis = this.kiGrafik.baueAnfrage(document, this.selectedRfaKuerzel,
          zielKarten.length > 0 ? zielKarten : umfang?.kartenIds ?? null, auswahl,
          umfang?.art === 'reiter' && auswahl !== 'kifelder', KiGrafikService.gebietFuer(zielId));
        if (!ergebnis.anfrage) {
          this.snackBar.open(`Keine KI-Texte erzeugt: ${ergebnis.grund}`, 'OK',
            {duration: 6000, panelClass: 'hinweis-fehler'});
          console.debug('KI-Grafik: keine Anfrage', ergebnis.grund);
          return;
        }
        // Die vorige Antwort **dieses Umfangs** verwerfen: bliebe sie während der neuen
        // Anfrage stehen, wäre nicht zu erkennen, welche der beiden man liest. Die
        // Antworten der anderen Zeilen bleiben stehen.
        this.kiErgebnisService.leeren(umfang);
        this.progressbarService.startShowProgressbar(1);
        this.progressbarService.onLoading(0);
        // MAX_CHARS kommt aus der Textbox, in die die Antwort geht – nicht geraten.
        const maxChars = zielId ? this.kiGrafik.maxCharsVon(document, zielId) : null;
        console.debug(`KI-Grafik: Anfrage mit ${ergebnis.boxen} angehakten Boxen aus `
          + `${umfang.art === 'zeile' ? 'Zeile' : 'Reiter'} "${umfang.name}", `
          + `max ${maxChars} Zeichen`, ergebnis.anfrage);
        // Zweite Anfrage: die Schlagzeile, aber **nur aus dem Reiter "Heute"**. Sie
        // beschreibt die aktuelle Lage; aus einem Aussichtentag heraus erzeugt, würde sie
        // die Schlagzeile von heute durch eine von übermorgen ersetzen. Sie hängt nicht
        // an dieser Antwort und läuft daneben.
        // Fortschritt neben genau dem Knopf, der gedrückt wurde (ausloeser, nicht umfang:
        // der Zeilen-Knopf schickt für den ganzen Reiter, die Anzeige gehört aber zu ihm).
        this.kiLaufService.neu(ausloeser);
        // NRW und jede Lokalzeit-Stadt haben ihre eigene Schlagzeile, ausgelöst aus "Heute".
        const schlagzeile = KiGrafikService.schlagzeileZu(zielId);
        if (schlagzeile) {
          this.erzeugeSchlagzeile(document, schlagzeile, undefined, umfang, ausloeser);
        }
        // … und aus "Nacht" nur mit dem Schalter "Schlagzeile erzeugen" (Oktober 2026): dann
        // dieselbe Schlagzeilen-Box, aber gespeist allein aus den Karten des Nacht-Reiters.
        const nachtSchlagzeile = KiGrafikService.nachtSchlagzeileZu(zielId);
        if (nachtSchlagzeile && this.schlagzeileSchalter.istAn(zielId)) {
          this.erzeugeSchlagzeile(document, nachtSchlagzeile, undefined, umfang, ausloeser, zielKarten);
        }
        const boxName = KiGrafikService.boxNameFuer(zielId);
        this.kiLaufService.begleite(ausloeser, boxName ?? umfang.name, anfrageId =>
          this.kiChatService.erzeugeTexte(ergebnis.anfrage!, maxChars, boxName, anfrageId)).subscribe({
          next: (antwort) => this.zeigeKiAntwort(document, antwort, ergebnis.boxen, umfang, zielId),
          error: (fehler) => {
            this.progressbarService.onError('KI-Texte konnten nicht erzeugt werden.');
            this.snackBar.open('Die KI-Anfrage ist fehlgeschlagen: '
              + KiChatService.fehlerUrsache(fehler), 'OK',
              {panelClass: 'hinweis-fehler'});
            console.error('ERROR: KI-Grafik-Anfrage fehlgeschlagen', fehler);
          },
          complete: () => {
            this.progressbarService.onLoading(1);
            this.progressbarService.stopShowProgressbar();
          }
        });
      }
    });
  }

  /**
   * Der Reiter-Umfang zu einem Zeilen-Umfang (die Karten des Reiters, zu dem die Zeile
   * gehört), ein Reiter-Umfang bleibt, wie er ist. Karten-Reihenfolge und Schlüssel
   * stimmen mit `umfangDesReiters()` überein, die Antwort landet also in der grünen Box
   * oben.
   */
  private reiterUmfangVon(document: DocumentModel, umfang: GrafikUmfang): GrafikUmfang {
    if (umfang?.art !== 'zeile') {
      return umfang;
    }
    return this.kiGrafik.reiterUmfangFuer(document, umfang) ?? umfang;
  }

  /**
   * Zeigt die KI-Antwort in der grünen Box ihres Umfangs und trägt sie in die Textbox
   * ihres Reiters ein.
   *
   * Die Box ist die eigentliche Rückmeldung: sie sagt, dass die Anfrage durchgegangen
   * ist, und zeigt den Text. Nötig, weil das Ziel der Schlagzeile in einem **anderen**
   * Reiter liegt (Langwetter) – von einem geglückten Durchlauf wäre an der Stelle des
   * Knopfes sonst nichts zu sehen. Eine Erfolgs-Hinweisleiste gibt es deshalb nicht
   * mehr; sie wäre nach sechs Sekunden weg, bevor der Text gelesen ist.
   *
   * Angezeigt wird auch, wenn das Zielfeld fehlt: die Antwort ist dann trotzdem da und
   * soll nicht verloren gehen – die Box nennt in diesem Fall den Fehlschlag der
   * Übernahme.
   */
  private zeigeKiAntwort(document: DocumentModel, antwort: string, boxen: number,
                         umfang: GrafikUmfang, zielId: string | null): void {
    const text = (antwort ?? '').trim();
    if (!text) {
      this.snackBar.open('Die KI hat keinen Text geliefert.', 'OK',
        {panelClass: 'hinweis-fehler'});
      return;
    }
    // Das Ziel kommt von der Aufrufstelle: beim Knopfdruck über den Grafiken aus dem
    // Reiter (Tabelle ZIELE), beim "Erneut Laden" von der Textbox selbst.
    const ziel = zielId ? this.kiGrafik.schreibeText(document, zielId, text) : null;
    if (ziel && text.length > (this.kiGrafik.maxCharsVon(document, zielId!) ?? Infinity)) {
      // Nur melden, nicht kürzen: was zu tun ist, entscheidet der Redakteur – und die
      // Box zeigt die Überlänge selbst an.
      console.warn(`KI-Text für "${ziel}" ist ${text.length} Zeichen lang und `
        + `überschreitet die Grenze der Textbox.`);
    }
    // Nur im KiErgebnisService-Signal, für die laufende Sitzung – nicht mehr im
    // Dokumentbaum: das machte jedes Speichern messbar langsamer (siehe
    // KiGrafikService.ladeErgebnisse), ohne dass die Box das für die aktuelle
    // Bearbeitung bräuchte.
    this.kiErgebnisService.ergaenze(umfang, text, boxen, ziel);
    if (!ziel) {
      this.snackBar.open('Der Text wurde erzeugt, aber keiner Textbox zugeordnet: zu '
        + `"${umfang.name}" gibt es keine im Reiter "KI-Texte".`, 'OK',
        {panelClass: 'hinweis-fehler'});
      return;
    }
    console.debug(`KI-Grafik: Text in "${ziel}" geschrieben`, text);
  }

  /**
   * "Erneut Laden" bzw. "Erneut Laden Text verkürzen" an einer Textbox des Reiters
   * "KI-Texte".
   *
   * Meldet, ob es diese Box war – sonst läuft der ältere Videotext-Weg weiter, der seine
   * Ziele über `KiButton.kiButtonMappings` findet. Die Grafik-Textboxen stehen in keinem
   * solchen Mapping: **ihre Anfrage wird aus der Box rekonstruiert**, nicht gemerkt.
   * `KiGrafikService.umfangFuerZiel()` dreht die `ZIELE`-Tabelle um, sammelt die Karten
   * des zugehörigen Reiters ein, und daraus entsteht dasselbe JSON wie beim Knopf über den
   * Grafiken – mit den Werten und Umschaltern, wie sie **jetzt** in den Karten stehen.
   *
   * ⚠️ Es wird immer der **Reiter**-Umfang geschickt, auch wenn die Box zuletzt von einem
   * Zeilen-Knopf gefüllt wurde: beide schreiben in dieselbe Textbox, aus ihr ist der
   * Unterschied nicht ablesbar – und der Reiter ist die vollständigere Antwort.
   *
   * `verkuerzt` senkt `MAX_CHARS` um 10 % (dieselbe Rechnung wie im Videotext-Weg). Der
   * bisherige Text der Box wird überschrieben.
   */
  private erneuereKiTextAusGrafik(document: DocumentModel, ereignis: KiRefreshEvent): boolean {
    const zielId = ereignis.containerId;
    const grenze = this.kiGrafik.maxCharsVon(document, zielId);
    const maxChars = grenze && ereignis.shortened
      ? grenze - Math.round(grenze / 10)
      : grenze ?? undefined;

    // Die Schlagzeile hängt an keinem Reiter – ihr Umfang sind alle Zeiträume, und sie
    // nimmt die eigene Wertauswahl (ohne Temperatur und Wind, außer bei Sturm).
    if (KiGrafikService.istSchlagzeile(zielId)) {
      console.debug(`KI-Grafik: Schlagzeile ${zielId} erneut anfordern, max ${maxChars} Zeichen`);
      this.erzeugeSchlagzeile(document, zielId, maxChars);
      return true;
    }

    const umfang = this.kiGrafik.umfangFuerZiel(document, zielId);
    if (!umfang) {
      return false;   // keine Grafik-Textbox – der Videotext-Weg ist zuständig
    }
    // Reiter-Umfang: dieselbe Zeilen-Auswahl wie der Knopf oben am Reiter.
    const auswahl = KiGrafikService.auswahlFuer(zielId);
    const ergebnis = this.kiGrafik.baueAnfrage(document, this.selectedRfaKuerzel,
      umfang.kartenIds, auswahl, auswahl !== 'kifelder', KiGrafikService.gebietFuer(zielId));
    if (!ergebnis.anfrage) {
      this.snackBar.open(`Kein KI-Text erzeugt: ${ergebnis.grund}`, 'OK',
        {duration: 6000, panelClass: 'hinweis-fehler'});
      return true;
    }
    this.kiErgebnisService.leeren(umfang);
    this.progressbarService.startShowProgressbar(1);
    this.progressbarService.onLoading(0);
    console.debug(`KI-Grafik: "${umfang.name}" erneut anfordern mit ${ergebnis.boxen} `
      + `Boxen, max ${maxChars} Zeichen`, ergebnis.anfrage);
    this.kiChatService.erzeugeTexte(ergebnis.anfrage, maxChars,
      KiGrafikService.boxNameFuer(zielId)).subscribe({
      next: (antwort) => this.zeigeKiAntwort(document, antwort, ergebnis.boxen, umfang, zielId),
      error: (fehler) => {
        this.progressbarService.onError('KI-Text konnte nicht erneut erzeugt werden.');
        this.snackBar.open('Die KI-Anfrage ist fehlgeschlagen: '
          + KiChatService.fehlerUrsache(fehler), 'OK',
          {panelClass: 'hinweis-fehler'});
        console.error('ERROR: KI-Grafik-Anfrage fehlgeschlagen', fehler);
      },
      complete: () => {
        this.progressbarService.onLoading(1);
        this.progressbarService.stopShowProgressbar();
      }
    });
    return true;
  }

  /**
   * Die **Schlagzeile**: ein grob zusammengefasster Text über alle Zeiträume.
   *
   * Ausgelöst wird sie **nur vom Reiter "Heute"** (siehe Aufrufstelle) – sie nennt die
   * aktuelle Lage, und aus einem Aussichtentag heraus erzeugt würde sie die Schlagzeile
   * von heute durch eine von übermorgen ersetzen. Ein Knopfdruck im Reiter "Heute" kostet
   * damit **zwei** Anfragen: eine für die Heute-Box, eine für die Schlagzeile.
   *
   * Sie gehört zu keinem einzelnen Reiter und kann deshalb nicht aus dessen Antwort
   * kommen – sie braucht eine **zweite** Anfrage, über die angehakten Boxen aller
   * Zeiträume ({@link KiGrafikService#kartenAllerZeitraeume}). Läuft neben der ersten,
   * nicht danach: die beiden hängen nicht voneinander ab, und hintereinander verdoppelte
   * sich die Wartezeit.
   *
   * ⚠️ Ihre Anfrage trägt **andere** Werte: `KiWertauswahl` `'schlagzeile'` lässt
   * Temperaturen und Wind weg – **außer bei Sturm** (ab Bft 9, siehe `STURM_AB`). Eine
   * Schlagzeile zählt keine Messwerte auf; ein Sturm gehört aber in jede.
   *
   * Ein Fehlschlag wird als rote Hinweisleiste gemeldet, mit Ursache (Zeitüberschreitung
   * oder HTTP-Status). Früher blieb er leise (nur `console`) – dann fiel nur zufällig auf,
   * dass die Schlagzeile fehlte, und nicht, warum. Ohne ein einziges angehaktes Symbol
   * (und ohne Sturm) entsteht gar keine Anfrage – auch das wird gemeldet, weil die
   * Heute-Anfrage seit dem Schalter "Temp" schon mit Temperaturen allein rausgeht.
   */
  private erzeugeSchlagzeile(document: DocumentModel, schlagzeile: string, maxCharsErsatz?: number,
                             umfang?: GrafikUmfang, ausloeser?: GrafikUmfang,
                             nurKarten?: string[]): void {
    // Aus "Nacht" (Schalter "Schlagzeile erzeugen") nur die Nacht-Karten, sonst alle Zeiträume.
    const karten = nurKarten?.length ? nurKarten : this.kiGrafik.kartenAllerZeitraeume(document, schlagzeile);
    const ergebnis = this.kiGrafik.baueAnfrage(document, this.selectedRfaKuerzel, karten,
      'schlagzeile', false, KiGrafikService.gebietFuer(schlagzeile));
    if (!ergebnis.anfrage) {
      // Sichtbar melden, nicht nur in der Konsole: seit dem Schalter "Temp" geht die
      // Heute-Anfrage auch ohne gelben Haken raus (Temperaturen genügen), die Schlagzeile
      // braucht aber ein angehaktes Symbol – sie blieb dann still aus, und im AI-Modul kam
      // nie eine Anfrage an.
      this.snackBar.open(`Keine Schlagzeile erzeugt: ${ergebnis.grund} `
        + 'Für die Schlagzeile mindestens ein Wetter-Symbol mit dem gelben Haken auswählen.', 'OK',
        {duration: 8000, panelClass: 'hinweis-fehler'});
      console.debug('KI-Grafik: keine Schlagzeile', ergebnis.grund);
      return;
    }
    const ergebnisBoxen = ergebnis.boxen;
    const maxChars = maxCharsErsatz
      ?? this.kiGrafik.maxCharsVon(document, schlagzeile);
    console.debug(`KI-Grafik: Schlagzeile aus ${ergebnis.boxen} Boxen aller Zeiträume, `
      + `max ${maxChars} Zeichen`, ergebnis.anfrage);
    const boxName = KiGrafikService.boxNameFuer(schlagzeile);
    const anfrage = ergebnis.anfrage;
    // Vom Knopf "KI-Texte erzeugen" aus erscheint sie als zweite Zeile in dessen
    // Fortschrittsanzeige; beim "Erneut Laden" an der Textbox gibt es keine Anzeige.
    const anfrage$ = ausloeser
      ? this.kiLaufService.begleite(ausloeser, boxName ?? 'Schlagzeile', anfrageId =>
        this.kiChatService.erzeugeTexte(anfrage, maxChars, boxName, anfrageId))
      : this.kiChatService.erzeugeTexte(anfrage, maxChars, boxName);
    anfrage$.subscribe({
      next: (antwort) => {
        const text = (antwort ?? '').trim();
        if (!text) {
          return;
        }
        const ziel = this.kiGrafik.schreibeText(document, schlagzeile, text);
        // Im Reiter "Heute" gehört sie in dieselbe grüne Box wie der Heute-Text: ein
        // Knopfdruck, zwei Texte. Angefügt, nicht ersetzt – die beiden Anfragen laufen
        // parallel und antworten in beliebiger Reihenfolge.
        if (umfang) {
          this.kiErgebnisService.ergaenze(umfang, text, ergebnisBoxen, ziel);
        }
        console.debug(ziel ? `KI-Grafik: Schlagzeile geschrieben` : 'KI-Grafik: keine Schlagzeilen-Box',
          text);
      },
      error: (fehler) => {
        const ursache = KiChatService.fehlerUrsache(fehler);
        this.snackBar.open(`Die Schlagzeile konnte nicht erzeugt werden (${ursache}).`, 'OK',
          {panelClass: 'hinweis-fehler'});
        console.error(`ERROR: Schlagzeile konnte nicht erzeugt werden (${ursache})`, fehler);
      }
    });
  }

  /**
   * Überträgt die Grafik-Werte des offenen Nordrhein-Westfalen-Reiters nach COSMO.
   *
   * Rückmeldung als Hinweisleiste: grün, wenn Karten übernommen wurden, rot wenn nicht.
   * Zeilen ohne Entsprechung in COSMO (dort gibt es weniger Zeitfenster) werden genannt,
   * damit niemand glaubt, sie seien mitgekommen.
   */
  public fuerCosmoUebernehmen(auftrag: CosmoUebernahme): void {
    this.wetterTerminalDocumentWrapper?.data$.pipe(first()).subscribe({
      next: (document) => {
        const ergebnis = this.cosmoTakeover.uebernehmen(document, auftrag.reiter, auftrag.zeile);
        // Schon gerenderte COSMO-Karten lesen die neuen Werte nicht von selbst.
        if (ergebnis.kartenIds.length > 0) {
          this.eventBusService.emit(EventBusTypes.GRAFIK_WERTE_GEAENDERT_EVENT.valueOf(),
            new GrafikUmfang(ergebnis.kartenIds, 'reiter', auftrag.reiter));
        }
        if (ergebnis.werte === 0) {
          this.snackBar.open(`Übernahme nach COSMO fehlgeschlagen: ${ergebnis.grund}`, 'OK',
            {panelClass: 'hinweis-fehler'});
          console.error('ERROR: Übernahme nach COSMO fehlgeschlagen', ergebnis);
          return;
        }
        const uebersprungen = ergebnis.uebersprungeneZeilen.length > 0
          ? ` Ohne Entsprechung in COSMO: ${ergebnis.uebersprungeneZeilen.join(', ')}.`
          : '';
        this.snackBar.open(`Für COSMO übernommen: ${ergebnis.werte} Werte in `
          + `${ergebnis.boxen} Boxen auf ${ergebnis.karten} Karten.${uebersprungen}`, 'OK',
          {duration: 6000, panelClass: 'hinweis-erfolg'});
        console.debug('Übernahme nach COSMO', ergebnis);
      }
    });
  }

  /**
   * "gespeicherte Daten laden": die TV-Werte der drei Aussichtentage in die Prognose-Karte(n)
   * des Umfangs. Meldung und Neuzeichnen macht der Service.
   */
  public gespeicherteDatenLaden(umfang: GrafikUmfang): void {
    this.wetterTerminalDocumentWrapper?.data$.pipe(first()).subscribe({
      next: (document) => this.prognoseUebernahme.uebernehmen(document, umfang.kartenIds ?? [])
    });
  }

  /**
   * "für Videotext übernehmen" / "für COSMO übernehmen" im Reiter "KI-Texte" von
   * Nordrhein-Westfalen: kopiert die ausgefüllten KI-Texte unverändert in die
   * gleichbedeutenden Boxen unter Videotext bzw. COSMOs eigenem Reiter "KI-Texte" – ohne
   * erneuten KI-Aufruf. Leer gebliebene Boxen werden übersprungen, keine Box unter
   * Videotext/COSMO wird deshalb geleert.
   */
  private kiTexteUebernehmen(ziel: KiTexteZiel): void {
    this.wetterTerminalDocumentWrapper?.data$.pipe(first()).subscribe({
      next: (document) => {
        const ergebnis = this.kiTexteTakeover.uebernehmen(document, ziel);
        const zielName = ziel === 'videotext' ? 'Videotext' : 'COSMO';
        if (ergebnis.felder === 0) {
          this.snackBar.open(`Übernahme für ${zielName} fehlgeschlagen: keine ausgefüllten KI-Texte gefunden.`,
            'OK', {panelClass: 'hinweis-fehler'});
          return;
        }
        const hinweise: string[] = [];
        if (ergebnis.uebersprungen.length > 0) {
          hinweise.push(`ohne Text: ${ergebnis.uebersprungen.join(', ')}`);
        }
        if (ergebnis.fehlend.length > 0) {
          hinweise.push(`Ziel-Box fehlt: ${ergebnis.fehlend.join(', ')}`);
        }
        const zusatz = hinweise.length > 0 ? ` (${hinweise.join('; ')})` : '';
        this.snackBar.open(`Für ${zielName} übernommen: ${ergebnis.felder} Text(e).${zusatz}`,
          'OK', {duration: 6000, panelClass: 'hinweis-erfolg'});
        console.debug(`KI-Texte-Übernahme für ${zielName}`, ergebnis);
      }
    });
  }

  ngOnDestroy() {
    if (!this.destroyer$.closed) {
      this.destroyer$.next(true);
      this.destroyer$.unsubscribe();
    }
  }

  /**
   * Wenn die documentId === "new", dann wird ein neues Dokument erstellt bzw. ein leeres Template geholt
   * @param documentId
   * @private
   */
  private getDocument(documentId: string, copyProfile?: string) {
    // Die grünen KI-Boxen gehören zum Datensatz, nicht zur Sitzung: der
    // KiErgebnisService lebt in der Wurzel und überdauert den Seitenwechsel. Beim Laden
    // wird deshalb erst alles verworfen und danach das eingetragen, was IM geladenen
    // Dokument steht. Ein NEUER Datensatz bringt nichts mit – dort bleiben die Boxen
    // also weg, und zwar auch, wenn eine Kopiervorlage Werte mitbringt.
    this.kiErgebnisService.alleLeeren();
    this.ladeQuellenService.alleLeeren();   // die Ladeberichte gehören zur Sitzung der Seite, nicht zum Datensatz
    this.schlagzeileSchalter.leeren();      // "Schlagzeile erzeugen" (Nacht) startet je Datensatz aus
    const neuerDatensatz = documentId === DocumentDetailComponent.NEUER_DATENSATZ;
    this.wetterTerminalDocumentWrapper = new ObservableWrapper(this.restService.loadDocumentById(this.selectedRfaKuerzel, documentId, copyProfile)
      .pipe(first(), tap((data) => {
        this.documentId = data.header.documentId as string;
        if (!neuerDatensatz) {
          this.ladeKiErgebnisse(data);
          // Reihenfolge der gelben Kästchen (ki:order:<boxId> an den Karten) kartenübergreifend einlesen
          this.kiReihenfolge.ladeAusDokument(data);
        }
      })));
  }

  /**
   * Trägt KI-Texte, die ein **älterer** Client im Datensatz abgelegt hat, wieder in ihre
   * grünen Boxen ein (siehe {@link KiGrafikService#ladeErgebnisse}).
   *
   * Ein neu erzeugtes Ergebnis wird nicht mehr abgelegt – nur im laufenden
   * `KiErgebnisService`-Signal gehalten –, weil das Mitspeichern in `image.values` jedes
   * Speichern messbar verlangsamte. Ein vor dieser Umstellung gespeicherter Datensatz
   * trägt den reservierten Schlüssel aber noch und soll seine Boxen beim Öffnen nicht
   * verlieren.
   */
  private ladeKiErgebnisse(document: DocumentModel): void {
    const gespeicherte = this.kiGrafik.ladeErgebnisse(document);
    gespeicherte.forEach(e => this.kiErgebnisService.setze(e.umfang, e.ergebnis));
    if (gespeicherte.length > 0) {
      console.debug(`KI-Grafik: ${gespeicherte.length} gespeicherte Antworten wiederhergestellt`);
    }
  }

  showActions() {
    this.wetterTerminalDocumentWrapper?.data$.subscribe({
      next: (document) => this.openActionSheet(document)
    });
  }

  /**
   * Öffnet das ActionSheet/Veröffentlichungsmenü für das Dokument
   * @param document
   * @private
   */
  private openActionSheet(document: DocumentModel) {

    const actionSheet = this.dialog.open(DocumentDetailActionComponent, {
      width: '750px',
      height: '500px',
      closeOnNavigation: true,
      data: {
        wetterTerminalDocument: document,
        workflowFilterCodes: document.tabs[this.selectedTabIndex].workflowFilterCodes,
      }
    });

    actionSheet.afterClosed().pipe(first()).subscribe({
      next: (value) => {
        if (value) this.route.navigate([`${P_DOCUMENTS}/${this.selectedRfaKuerzel}`]);
      }
    });
  }

  /**
   * Trägt dieser Reiter selbst wieder Reiter?
   *
   * Die Lokalzeit-Städte sind dreistufig: Lokalzeiten → Aachen → Heute/Nacht/…/KI-Texte.
   * Geprüft wird am ersten Kind, wie schon auf der Ebene darüber – im Template sind die
   * Kinder eines Reiters entweder alle Reiter oder alle Sections.
   */
  public hatUnterreiter(container: Container): boolean {
    return container.containerList?.[0]?.containerType === 'Tab';
  }

  /**
   * Eine Snackbar für alle Karten eines Knopfdrucks, die in ihrer MOS-Datei keine Werte
   * fanden. Genannt werden die Zeiträume ("HEUTE", "1. AUSSICHTENTAG" – der Teil vor dem
   * Doppelpunkt, sonst stünden sechs Heute-Zeilen da) und die Datei, damit klar ist, dass
   * nicht der Abruf gescheitert ist, sondern die Datei diese Zeile nicht kennt.
   */
  private meldeMosOhneWerte(meldungen: MosOhneWerte[]): void {
    const zeitraeume = [...new Set(meldungen.map(m => m.zeile.split(':')[0].trim()).filter(z => !!z))];
    const dateien = [...new Set(meldungen.map(m => m.datei))];
    const quelle = dateien.length === 1 ? dateien[0] : `die MOS-Dateien (${dateien.length})`;
    this.snackBar.open(`MSWR-MOS: für ${zeitraeume.join(', ') || 'diese Zeile'} enthält ${quelle} `
      + 'keine Werte – die MOS-Dateien kennen nur heute Nacht und morgen.', 'OK', {duration: 8000});
  }

  reload = () => this.getDocument(this.documentId);
  protected readonly indexedDB = indexedDB;

  public getIndexOfNonTabElement(tab:Tab):number {

    if(tab.containerList && tab.containerList.length > 0 && tab.containerList[0].containerType === 'Tab') {
      for(let i = 0; i<tab.containerList.length; i++) {
        if(tab.containerList[i].containerType !== 'Tab') {
          return i;
        }
      }
    }
    return -1;
  }

  doKiEvent(kiButtons: KiButtonMapping[], tabs:Tab[]|undefined) {
    let counter:number = 0;
    //console.log("KI-BUTTON_MAPPING: " + JSON.stringify(kiButtons))
    this.progressbarService.startShowProgressbar(kiButtons.length);
    this.progressbarService.onLoading(counter);
    kiButtons.forEach(button => {
      tabs?.forEach((tab:Tab) => {
        let sourceContainer:Container|null = this.findContainerById(tab, button.from);
        if(sourceContainer && this.layoutElementUtil.isTextArea(sourceContainer)) {
          let content:string|undefined = this.layoutElementUtil.asTextArea(sourceContainer).content;
          if(content && content.length > 0) {
            this.restService.getKiTextForHessenwetter(content, button).subscribe({
              next: response => {
                //console.log("KI Ergebnis: " + JSON.stringify(response));
                this.setKiTextByContainerId(response, tabs, button.to);
              },
              error: err => {
                console.debug("So ein Fuck: " + JSON.stringify(err));
                this.progressbarService.onError("Kann Box '" + button.to + "' nicht füllen.");
              }, complete: () => {
                counter++;
                this.progressbarService.onLoading(counter);
                if(counter >= kiButtons.length) {
                  this.progressbarService.stopShowProgressbar();
                }
                console.debug("The KI observable is now completed.");
              }
            });
          }
        }
      })
    });
  }

  /**
   * Schreibt das KI-Ergebnis in das Ziel-Textfeld.
   *
   * Sucht den Container anhand seiner containerId rekursiv im gesamten Baum. Vorher war
   * die Suche auf den Reiter "KI-Videotext" und auf direkte Kinder einer Section begrenzt;
   * damit blieben Ziele in anderen Reitern (z.B. WDR "Videotext") sowie Felder innerhalb
   * eines TextAreaContainers unerreichbar. Die containerIds sind dokumentweit eindeutig,
   * deshalb ist die Suche über alle Reiter eindeutig.
   */
  private setKiTextByContainerId(text: string, tabs: Tab[], containerId: string) {
    if (!tabs || tabs.length === 0) {
      return;
    }
    for (const tab of tabs) {
      const target = this.findContainerById(tab, containerId);
      if (target && this.layoutElementUtil.isTextArea(target)) {
        this.layoutElementUtil.asTextArea(target).content = text;
        return;
      }
    }
    console.warn(`KI-Ergebnis konnte nicht zugeordnet werden: kein TextArea mit containerId ${containerId}`);
  }

  getLokalzeitenEvent(ws: String[], ac: Container, lokalzeiten: Container) {
    if (ac && lokalzeiten && ws.length > 0) {
      ws.forEach(w => {
        let ids = w.split(',');
        if (ids.length == 2) {
          let textToCopyFrom: string | undefined;
          ac.containerList?.forEach(container => {
            if (container.containerType === 'Section' && container.caption === 'TV Vorhersage') {
              container.containerList?.forEach(c => {
                c.containerList?.forEach(co => {
                  if (this.layoutElementUtil.isTextArea(co) && co.containerId === ids[0]) {
                    textToCopyFrom = this.layoutElementUtil.asTextArea(co).content;
                  }
                })
              });
            }
          })
          if (textToCopyFrom) {
            lokalzeiten.containerList?.forEach(subtab => {
              if (subtab.caption != ac.caption) {
                const tab = this.layoutElementUtil.asTab(subtab);
                if (tab) {
                  tab.containerList?.forEach(container => {
                    if (container.containerType === 'Section' && container.caption === 'TV Vorhersage') {
                      container.containerList?.forEach(c => {
                        if (this.layoutElementUtil.isTextArea(c) && c.containerId === ids[1]) {
                          const textArea = this.layoutElementUtil.asTextArea(c);
                          textArea.content = textToCopyFrom;
                        }
                      });
                    }
                  });
                }
              }
            })
          }
        }
      })
    }
  }

  findContainerByContainerType(root: Container, target:string): Container | null {
    // 1. Check if the current node is the one we're looking for
    if (root.containerType === target) {
      return root;
    }

    // 2. If it has children, search through them
    if (root.containerList && root.containerList.length > 0) {
      for (const child of root.containerList) {
        const found = this.findContainerByContainerType(child, target);

        // 3. If the recursive call found the node, bubble it up
        if (found) {
          return found;
        }
      }
    }

    // 4. Return null if nothing was found in this branch
    return null;
  }

  findContainerById(root: Container, targetId: string): Container | null {
    // 1. Check if the current node is the one we're looking for
    if (root.containerId === targetId) {
      return root;
    }

    // 2. If it has children, search through them
    if (root.containerList && root.containerList.length > 0) {
      for (const child of root.containerList) {
        const found = this.findContainerById(child, targetId);

        // 3. If the recursive call found the node, bubble it up
        if (found) {
          return found;
        }
      }
    }

    // 4. Return null if nothing was found in this branch
    return null;
  }

  protected readonly Object = Object;
}
